import React, { useCallback, useMemo, useState } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow, Circle } from '@react-google-maps/api';
import {
  Box, Typography, CircularProgress, Alert, Stack, Chip,
} from '@mui/material';
import { useDashboard } from '../../../contexts/DashboardContext';
import {
  DEFAULT_CLEARANCE_COLORS,
  clearanceColor,
} from '../../../data/sfAdvisorIntake';

const containerStyle = {
  width: '100%',
  height: '100%',
  minHeight: 280,
};

/** San Francisco civic center — default for permit / parcel apps */
const SF_CENTER = {
  lat: 37.7749,
  lng: -122.4194,
};

function pinIcon(status, colors) {
  const { fill, stroke } = clearanceColor(status, colors);
  const svg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">`
    + `<path fill="${fill}" stroke="${stroke}" stroke-width="2" d="M18 1C9.2 1 2 8.2 2 17c0 12.4 16 30 16 30s16-17.6 16-30C34 8.2 26.8 1 18 1z"/>`
    + `<circle cx="18" cy="17" r="6" fill="#fff"/>`
    + `</svg>`,
  );
  return {
    url: `data:image/svg+xml;charset=UTF-8,${svg}`,
    scaledSize: typeof window !== 'undefined' && window.google?.maps
      ? new window.google.maps.Size(36, 48)
      : undefined,
    anchor: typeof window !== 'undefined' && window.google?.maps
      ? new window.google.maps.Point(18, 48)
      : undefined,
  };
}

async function reverseGeocode(lat, lng) {
  const url = `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/reverseGeocode?${new URLSearchParams({
    location: JSON.stringify({ x: lng, y: lat }),
    langCode: 'en',
    outSR: '4326',
    f: 'json',
  })}`;
  const res = await fetch(url);
  const data = await res.json();
  const addr = data?.address;
  if (!addr) return null;
  const line = addr.Match_addr || addr.LongLabel || addr.Address
    || [addr.Address, addr.City, addr.Region, addr.Postal].filter(Boolean).join(', ');
  return line || null;
}

export default function DashboardMap({
  panelId, outputKey = 'map_markers', config,
}) {
  const {
    getSlice, setOutput, setPanelContext, validateParcelAddress, structuredOutput, config: appConfig,
  } = useDashboard();
  const raw = getSlice(panelId, outputKey);
  const interactive = Boolean(config?.interactivePick ?? config?.interactive);
  const showLegend = Boolean(config?.showClearanceLegend ?? true);
  const defaultZoom = Number(config?.defaultZoom) || 12;
  const emptyCenter = config?.defaultCenter || SF_CENTER;
  const clearancePalette = config?.clearanceColors || DEFAULT_CLEARANCE_COLORS;
  const jurisdictionId = appConfig?.metadata?.interviewFlow?.jurisdictionId || 'san-francisco';
  const parcelAdapter = appConfig?.metadata?.interviewFlow?.parcelAdapter
    || (jurisdictionId === 'san-francisco' ? 'sf-planning-gis' : 'none');
  const liveGis = parcelAdapter === 'sf-planning-gis';
  const cityLabel = String(appConfig?.name || jurisdictionId)
    .replace(/\s+Application Advisor$/i, '')
    .trim() || 'this city';

  const landUseStatus = structuredOutput?.parcel?.landUse?.status
    || raw?.clearance?.status
    || null;

  const { center, markers, zoom, clearance } = useMemo(() => {
    if (!raw || typeof raw !== 'object') {
      return {
        center: emptyCenter, markers: [], zoom: defaultZoom, clearance: null,
      };
    }
    const c = raw.center || raw.map_center || emptyCenter;
    const m = Array.isArray(raw.markers) ? raw.markers : [];
    const z = raw.zoom || (m.length ? 16 : defaultZoom);
    return {
      center: c, markers: m, zoom: z, clearance: raw.clearance || null,
    };
  }, [raw, emptyCenter, defaultZoom]);

  const [selectedMarker, setSelectedMarker] = useState(null);
  const [pickMarker, setPickMarker] = useState(null);
  const [pickBusy, setPickBusy] = useState(false);
  const [pickHint, setPickHint] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);

  const applyAddressPick = useCallback(async (lat, lng) => {
    setPickBusy(true);
    setPickHint(null);
    setPickMarker({ lat, lng });
    try {
      const address = await reverseGeocode(lat, lng);
      const label = address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      setPickMarker({
        lat, lng, label, title: 'Selected location', description: label,
      });
      setPanelContext?.('mapPick', {
        address: label, lat, lng, at: new Date().toISOString(),
      });
      setOutput?.((prev) => ({
        ...prev,
        form_prefill: {
          ...(prev?.form_prefill || {}),
          address: label,
          location: label,
          lat,
          lng,
        },
        map_markers: {
          center: { lat, lng },
          zoom: 17,
          markers: [{
            lat, lng, label, title: 'Selected parcel location', description: label,
          }],
        },
      }));
      const prefill = structuredOutput?.form_prefill || {};
      const validated = await validateParcelAddress?.({
        address: label,
        lon: lng,
        lat,
        proposedUse: prefill.proposedUse,
        description: prefill.description,
        propertyType: prefill.propertyType || prefill.projectType,
      });
      if (validated?.success) {
        const status = validated?.landUse?.statusLabel || validated?.landUse?.status;
        setPickHint(status
          ? `Validated · land-use: ${status}`
          : `Validated: ${validated.location || label}`);
      } else if (validated?.error) {
        setPickHint(validated.error);
      } else {
        setPickHint(address ? `Address set: ${address}` : 'Location pinned — enter the street address in the form if needed.');
      }
    } catch (e) {
      setPickHint(e?.message || 'Could not reverse-geocode this point');
    } finally {
      setPickBusy(false);
    }
  }, [setOutput, setPanelContext, validateParcelAddress, structuredOutput?.form_prefill]);

  const onMapClick = useCallback((e) => {
    if (!interactive) return;
    const lat = e?.latLng?.lat?.();
    const lng = e?.latLng?.lng?.();
    if (lat == null || lng == null) return;
    applyAddressPick(lat, lng);
  }, [interactive, applyAddressPick]);

  const displayMarkers = markers.length
    ? markers
    : (pickMarker ? [pickMarker] : []);
  const mapCenter = displayMarkers[0]
    ? { lat: displayMarkers[0].lat || displayMarkers[0].latitude, lng: displayMarkers[0].lng || displayMarkers[0].longitude }
    : center;

  const ringStatus = clearance?.status || landUseStatus || displayMarkers[0]?.clearanceStatus;
  const ringColors = ringStatus ? clearanceColor(ringStatus, clearancePalette) : null;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  if (!apiKey) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">Google Maps API key not configured</Typography>
        {interactive && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter a street address in the form above to continue.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 280, display: 'flex', flexDirection: 'column' }}>
      {interactive && (
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {liveGis
              ? 'Click the map to pin a parcel — pin color reflects land-use clearance when a proposed use is set.'
              : `Map is centered on ${cityLabel}. Catalog-only mode — pin for context; application path comes from the city package (no live GIS).`}
          </Typography>
          {pickBusy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption">Looking up address…</Typography>
            </Box>
          )}
          {pickHint && !pickBusy && (
            <Alert severity="info" sx={{ mt: 0.5, py: 0 }}>{pickHint}</Alert>
          )}
        </Box>
      )}
      {showLegend && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Clearance:
          </Typography>
          {Object.entries(clearancePalette).filter(([k]) => k !== 'unknown').map(([key, meta]) => (
            <Chip
              key={key}
              size="small"
              label={meta.label}
              sx={{
                height: 22,
                bgcolor: meta.fill,
                color: '#fff',
                fontWeight: 600,
                opacity: ringStatus && clearanceColor(ringStatus, clearancePalette).fill === meta.fill ? 1 : 0.55,
              }}
            />
          ))}
        </Stack>
      )}
      {!interactive && !displayMarkers.length && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.75 }}>
          {liveGis
            ? 'Map ready — run advise with an address to pin the parcel.'
            : `Map shows ${cityLabel} — enter an address and submit for package-based guidance.`}
        </Typography>
      )}
      <Box sx={{ flex: 1, minHeight: 260 }}>
        <LoadScript googleMapsApiKey={apiKey} onLoad={() => setMapsReady(true)}>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={zoom}
            onClick={onMapClick}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              clickableIcons: false,
            }}
          >
            {ringColors && displayMarkers[0] && mapsReady && (
              <Circle
                center={{
                  lat: displayMarkers[0].lat || displayMarkers[0].latitude,
                  lng: displayMarkers[0].lng || displayMarkers[0].longitude,
                }}
                radius={45}
                options={{
                  fillColor: ringColors.fill,
                  fillOpacity: 0.22,
                  strokeColor: ringColors.stroke,
                  strokeOpacity: 0.9,
                  strokeWeight: 2,
                  clickable: false,
                }}
              />
            )}
            {displayMarkers.map((marker, i) => {
              const status = marker.clearanceStatus || ringStatus;
              const icon = mapsReady ? pinIcon(status, clearancePalette) : undefined;
              return (
                <Marker
                  key={i}
                  position={{ lat: marker.lat || marker.latitude, lng: marker.lng || marker.longitude }}
                  onClick={() => setSelectedMarker(marker)}
                  title={marker.label || marker.title || `Marker ${i + 1}`}
                  icon={icon}
                />
              );
            })}
            {selectedMarker && (
              <InfoWindow
                position={{
                  lat: selectedMarker.lat || selectedMarker.latitude,
                  lng: selectedMarker.lng || selectedMarker.longitude,
                }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <Box>
                  <Typography variant="subtitle2">
                    {selectedMarker.label || selectedMarker.title || 'Marker'}
                  </Typography>
                  {selectedMarker.description && (
                    <Typography variant="body2">{selectedMarker.description}</Typography>
                  )}
                  {(selectedMarker.clearanceLabel || clearance?.statusLabel || ringStatus) && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                      Land use:
                      {' '}
                      {selectedMarker.clearanceLabel
                        || clearance?.statusLabel
                        || clearanceColor(ringStatus, clearancePalette).label}
                    </Typography>
                  )}
                </Box>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </Box>
    </Box>
  );
}
